import { ShieldCheck, UsersRound } from "lucide-react";
import { TestUserCleanup } from "@/components/admin/TestUserCleanup";
import { formatStoredDate } from "@/lib/date";
import { useAdminUsers } from "@/hooks/useAdminUsers";

const formatSignupDate = (value) => new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const formatLastReflection = (date) => date ? formatStoredDate(date, { month: "short", day: "numeric", year: "numeric" }) : "None yet";

export const AdminUsersPage = () => {
  const { error, loading, reloadUsers, users } = useAdminUsers();
  const totalReflections = users.reduce((sum, user) => sum + user.reflection_count, 0);
  const remindersEnabled = users.filter((user) => user.reminder_enabled).length;

  return (
    <main className="devotional-shell min-h-screen px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-users-page">
      <div className="mx-auto max-w-7xl space-y-8">
        <section className="gentle-reveal" data-testid="admin-header-section">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8C9B90]" data-testid="admin-overline">Admin</p>
          <h1 className="mt-3 font-serif-display text-4xl font-semibold leading-tight text-[#1A3626] sm:text-5xl lg:text-6xl" data-testid="admin-heading">Account overview</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#4A5D4E]" data-testid="admin-copy">View who has created an account, how many reflections they have saved, their last reflection date, and reminder status.</p>
        </section>

        <section className="grid gap-4 sm:grid-cols-3" data-testid="admin-stats-grid">
          <article className="rounded-2xl border border-[#E5E1D8] bg-white p-5 shadow-[0_8px_30px_rgba(26,54,38,0.04)]" data-testid="admin-total-users-card">
            <UsersRound className="text-[#C87961]" size={24} />
            <p className="mt-4 text-sm text-[#8C9B90]" data-testid="admin-total-users-label">Total users</p>
            <p className="font-serif-display text-4xl font-semibold text-[#1A3626]" data-testid="admin-total-users-value">{users.length}</p>
          </article>
          <article className="rounded-2xl border border-[#E5E1D8] bg-white p-5 shadow-[0_8px_30px_rgba(26,54,38,0.04)]" data-testid="admin-total-reflections-card">
            <ShieldCheck className="text-[#C87961]" size={24} />
            <p className="mt-4 text-sm text-[#8C9B90]" data-testid="admin-total-reflections-label">Total reflections</p>
            <p className="font-serif-display text-4xl font-semibold text-[#1A3626]" data-testid="admin-total-reflections-value">{totalReflections}</p>
          </article>
          <article className="rounded-2xl border border-[#E5E1D8] bg-white p-5 shadow-[0_8px_30px_rgba(26,54,38,0.04)]" data-testid="admin-reminders-card">
            <ShieldCheck className="text-[#C87961]" size={24} />
            <p className="mt-4 text-sm text-[#8C9B90]" data-testid="admin-reminders-label">Reminders enabled</p>
            <p className="font-serif-display text-4xl font-semibold text-[#1A3626]" data-testid="admin-reminders-value">{remindersEnabled}</p>
          </article>
        </section>

        <TestUserCleanup onCleanupComplete={reloadUsers} />

        <section className="overflow-hidden rounded-2xl border border-[#E5E1D8] bg-white shadow-[0_8px_30px_rgba(26,54,38,0.04)]" data-testid="admin-users-table-section">
          {loading ? (
            <div className="flex min-h-64 items-center justify-center font-serif-display text-3xl text-[#1A3626]" data-testid="admin-loading-state">Loading accounts…</div>
          ) : error ? (
            <div className="p-6 text-[#A85F4B]" data-testid="admin-error-message">{error}</div>
          ) : (
            <div className="overflow-x-auto" data-testid="admin-users-table-wrapper">
              <table className="w-full min-w-[920px] text-left" data-testid="admin-users-table">
                <thead className="bg-[#F9F6F0] text-xs uppercase tracking-[0.16em] text-[#8C9B90]" data-testid="admin-users-table-head">
                  <tr>
                    <th className="px-5 py-4" data-testid="admin-table-heading-user">User</th>
                    <th className="px-5 py-4" data-testid="admin-table-heading-signup">Signup</th>
                    <th className="px-5 py-4" data-testid="admin-table-heading-count">Reflections</th>
                    <th className="px-5 py-4" data-testid="admin-table-heading-last">Last reflection</th>
                    <th className="px-5 py-4" data-testid="admin-table-heading-reminder">Reminder</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E1D8]" data-testid="admin-users-table-body">
                  {users.map((user) => (
                    <tr key={user.id} className="text-sm text-[#4A5D4E]" data-testid={`admin-user-row-${user.id}`}>
                      <td className="px-5 py-4" data-testid={`admin-user-${user.id}-identity`}>
                        <p className="font-semibold text-[#1A3626]" data-testid={`admin-user-${user.id}-name`}>{user.name}{user.is_admin ? " · Admin" : ""}</p>
                        <p data-testid={`admin-user-${user.id}-email`}>{user.email}</p>
                      </td>
                      <td className="px-5 py-4" data-testid={`admin-user-${user.id}-signup`}>{formatSignupDate(user.created_at)}</td>
                      <td className="px-5 py-4 font-semibold text-[#1A3626]" data-testid={`admin-user-${user.id}-reflection-count`}>{user.reflection_count}</td>
                      <td className="px-5 py-4" data-testid={`admin-user-${user.id}-last-reflection`}>{formatLastReflection(user.last_reflection_date)}</td>
                      <td className="px-5 py-4" data-testid={`admin-user-${user.id}-reminder`}>
                        {user.reminder_enabled ? `On · ${user.reminder_frequency}` : "Off"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};